import React, { useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import arrowBackIcon, {
  ReactComponent as ArrowBackIcon,
} from "../../../assets/icons/arrow-back.svg";
import { ReactComponent as EmailSuccessIcon } from "../../../assets/icons/email-success.svg";
import { resetPasswordAction } from "../Actions/CommonActions";
import BaseButton from "../Common/Buttons/BaseButton";
import { LoadingSlides } from "../CommonComponents/Common";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import { ReactComponent as SuccessIcon } from "../../../assets/Images/SuccessProjectIcon.svg";
import {
  getDecryptedItem,
  setEncryptedItem,
} from "../../../utils/localStorageHelper";
import ErrorPopUp from "../CommonComponents/PopUpCards/ErrorPopUp";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [email, setEmail] = useState("");
  const [spin, setSpin] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [passwordPopup, setPasswordPopup] = useState(false);
  const [errorObj, setErrorObj] = useState({
    show: false,
    errMsg: "",
  });

  useEffect(() => {
    const storedData = getDecryptedItem("passwordReset") || [];
    const userData = storedData?.find((user) => user.email === email) || {};
    setAttempt(userData?.attempt || 0);
    setTimeLeft(
      userData?.lockoutUntil > Date.now()
        ? Math.floor((userData?.lockoutUntil - Date.now()) / 1000)
        : 0
    );
  }, [email]);

  useEffect(() => {
    if (timeLeft > 0) {
      const interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            let storedData = getDecryptedItem("passwordReset") || [];
            storedData = storedData.map((user) =>
              user?.email === email ? { ...user, lockoutUntil: 0 } : user
            );
            setEncryptedItem("passwordReset", storedData);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timeLeft, email]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setSpin(true);

    const trimmedEmail = email?.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
      setErrorObj({
        show: true,
        errMsg: "Please enter a valid email address",
      });
      setSpin(false);
      return;
    }

    let storedData = getDecryptedItem("passwordReset") || [];
    let userData = storedData?.find((user) => user.email === trimmedEmail);
    let newAttempt = userData ? userData?.attempt + 1 : 1;

    let res: any = await dispatch(resetPasswordAction({ email: email.trim() }));
    if (res?.status !== 200) {
      setEmailSent(false);
      setErrorObj({
        show: true,
        errMsg: res?.errMsg,
      });
    } else {
      setEmailSent(true);
      setPasswordPopup(true);

      let lockoutDuration =
        newAttempt === 1 ? 90 : newAttempt === 2 ? 120 : 3600;
      const lockoutUntil = Date.now() + lockoutDuration * 1000;

      if (userData) {
        userData.attempt = newAttempt;
        userData.lockoutUntil = lockoutUntil;
      } else {
        storedData.push({
          email: trimmedEmail,
          attempt: newAttempt,
          lockoutUntil,
        });
      }

      setEncryptedItem("passwordReset", storedData);
      setAttempt(newAttempt);
      setTimeLeft(lockoutDuration);
    }
    setSpin(false);
  };

  const togglePasswordPopup = () => {
    setPasswordPopup((prev) => !prev);
  };

  const handleBack = () => {
    setEmailSent(false);
    setEmail("");
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <Box className="main-container">
      {emailSent ? (
        <Box className="flex-column-center">
          <EmailSuccessIcon />
          <Typography className="font-18-700-grey" gutterBottom>
            Verification Email Sent
          </Typography>
          <Typography className="font-13-400-grey" gutterBottom>
            We sent a password reset mail to{" "}
            <span
              style={{
                fontWeight: 600,
              }}
            >
              {email}
            </span>
          </Typography>
          <Typography
            className="font-13-400-grey"
            sx={{
              mb: 2,
            }}
          >
            Please check your mail
          </Typography>
          <BaseButton
            title="Go Back"
            handleClick={handleBack}
            startIcon={<ArrowBackIcon />}
            styles={{ color: "#1A1D23", fontSize: 14, fontWeight: 400 }}
          />
          {passwordPopup && (
            <CommonPopup
              message={`Verification Email has been sent to your Email Id, Please verify to continue, if not received please check once on spam folder`}
              show={passwordPopup}
              toggle={togglePasswordPopup}
              Icon={SuccessIcon}
              heading=""
            />
          )}
        </Box>
      ) : (
        <>
          <Typography className="font-28-700-grey ">
            Forgot Password?
          </Typography>
          <Typography
            className="font-14-grey2"
            sx={{
              mb: 2,
            }}
          >
            No worries we will send you a reset link
          </Typography>
          <Box
            sx={{
              mb: 2,
            }}
          >
            <Typography className="font-14-grey mb-1">Email</Typography>
            <TextField
              name="email"
              value={email?.toLowerCase()}
              onChange={(e) => {
                setEmail(e.target.value.trim().toLowerCase());
              }}
              variant="outlined"
              fullWidth
              placeholder="Enter your registered email"
              sx={commonStyles.inputField}
            />
          </Box>

          <Button
            onClick={handleResetPassword}
            disabled={spin || attempt > 3 || timeLeft > 0}
            variant="contained"
            sx={commonStyles.loginButton}
            fullWidth
          >
            {spin ? <LoadingSlides bgColor="white" /> : "Reset Password"}
          </Button>
          {timeLeft > 0 && (
            <Typography
              color="error"
              sx={{
                textAlign: "center",
                mt: 2,
              }}
            >
              Please wait {formatTime(timeLeft)} min. before retrying.
            </Typography>
          )}
          <Typography
            className="font-14-400-dark-grey"
            sx={{
              textAlign: "center",
              mt: 3,
            }}
          >
            <Box
              component={"span"}
              onClick={() => history.push("/login")}
              className="flex-xy-center"
              sx={{
                textDecoration: "none",
                color: "#1A1D23",
                cursor: "pointer",
              }}
            >
              <img
                src={arrowBackIcon}
                alt="arrow back"
                style={{ marginRight: 4 }}
              />
              Back to login
            </Box>
          </Typography>

          <ErrorPopUp
            show={errorObj.show}
            toggle={() =>
              setErrorObj((prev) => ({
                ...prev,
                show: false,
                errMsg: "",
              }))
            }
            heading={"Error"}
            message={errorObj?.errMsg}
            spin={spin}
          />
        </>
      )}
    </Box>
  );
};

export default ForgotPassword;
